import { apiRequest } from '../../shared/api/client';
import { getRuntimeConfig } from '../../shared/config/runtime';

/**
 * Wire-format types for the four IT-API-Claude registration endpoints.
 * Mirror exactly Invetrak.Api.Endpoints.RegistrationEndpoints. Keep
 * casing as the server emits it (camelCase via System.Text.Json defaults).
 */

export interface LegalDocumentSummary {
  legalDocumentId: string;
  documentType: string;          // "tos" | "privacy" | future codes
  version: string;
  effectiveAtUtc: string;
  contentHash: string;
}

export interface StartRegistrationRequest {
  businessName: string;
  contactEmail: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone?: string | null;
  expectedWarehouseCount?: number | null;
  acceptedLegalDocumentIds: string[];
  turnstileToken?: string | null;
  /**
   * The user's chosen UI locale. Not yet honored by the server (planned in
   * M3.9 backend work) — passing it now is forward-compatible because
   * System.Text.Json silently ignores unknown JSON properties by default,
   * so the request still succeeds on the current server.
   */
  preferredCulture?: string;
}

export interface StartRegistrationResponse {
  tenantRegistrationId: string;
  contactEmail: string;
  verificationTokenExpiresAtUtc: string;
}

export interface VerifyTokenResponse {
  tenantRegistrationId: string;
  businessName: string;
  contactEmail: string;
  contactFirstName: string;
  contactLastName: string;
  status: string;                 // "pending_email" | "verifying" | ...
  verificationTokenExpiresAtUtc: string;
}

export interface CompleteRegistrationResponse {
  tenantRegistrationId: string;
  userId: string;
  email: string;
  displayName: string;
}

// All registration calls need cookies set/sent for the post-Complete sign-in.
const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function getLegalDocuments(signal?: AbortSignal): Promise<LegalDocumentSummary[]> {
  const { data } = await apiRequest<LegalDocumentSummary[]>(
    '/v1/registration/legal-documents',
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function startRegistration(
  body: StartRegistrationRequest,
  signal?: AbortSignal,
): Promise<StartRegistrationResponse> {
  // THE TOKEN DOES NOT COME BACK, and must not. It used to arrive in the 202's
  // Location header, which meant whoever called this endpoint could finish the
  // registration without ever seeing the mailbox -- so a tenant could be created
  // under an address nobody had proven they owned. The emailed link is the only
  // way to step 3 now.
  //
  // This still makes its own fetch rather than using apiRequest, for a different
  // reason: the registration screens branch on RegistrationError codes taken from
  // the problem+json `type`, and apiRequest throws a generic ApiError.
  const correlationId = crypto.randomUUID();
  // Resolve the base the same way `apiRequest` does: dev → "/api" (Vite
  // proxy), prod → runtime config. We can't reuse apiRequest itself because
  // it doesn't expose response headers (we need Location to extract the
  // verification token).
  const envOverride = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const base = envOverride
    ? `${envOverride.replace(/\/+$/, '')}/api`
    : import.meta.env.DEV
      ? '/api'
      : `${getRuntimeConfig().apiBaseUrl}/api`;
  const res = await fetch(`${base}/v1/registration/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Correlation-Id': correlationId,
    },
    body: JSON.stringify(body),
    signal,
    ...COOKIE_INIT,
  });
  if (!res.ok) {
    let problem: unknown;
    try { problem = await res.json(); } catch { /* ignore */ }
    throw new RegistrationError(res.status, problem);
  }
  return (await res.json()) as StartRegistrationResponse;
}

export async function getRegistrationByToken(
  token: string,
  signal?: AbortSignal,
): Promise<VerifyTokenResponse> {
  const { data } = await apiRequest<VerifyTokenResponse>(
    `/v1/registration/verify/${encodeURIComponent(token)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function completeRegistration(
  token: string,
  password: string,
  signal?: AbortSignal,
): Promise<CompleteRegistrationResponse> {
  const { data } = await apiRequest<CompleteRegistrationResponse>(
    `/v1/registration/verify/${encodeURIComponent(token)}/complete`,
    { method: 'POST', body: { password }, signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Lightweight error wrapper so the StartRoute can distinguish between
 * "email already in use" (409 / error code = email_in_use) and "generic
 * 400" without sprinkling ApiError type guards everywhere. The Problem
 * Details payload travels through `problem`.
 */
export class RegistrationError extends Error {
  public readonly status: number;
  public readonly problem: { type?: string; title?: string; detail?: string; errorCode?: string } | null;

  public constructor(status: number, problem: unknown) {
    const p = (problem ?? {}) as { title?: string; detail?: string; type?: string; errorCode?: string };
    super(p.detail ?? p.title ?? `HTTP ${status}`);
    this.name = 'RegistrationError';
    this.status = status;
    this.problem = (problem as RegistrationError['problem']) ?? null;
  }

  public get code(): 'email_in_use' | 'turnstile_failed' | 'generic' {
    const t = this.problem?.type ?? '';
    if (this.status === 409 || t.endsWith('/email-in-use') || this.problem?.errorCode === 'email_in_use') {
      return 'email_in_use';
    }
    if (this.status === 403 || t.endsWith('/turnstile-failed') || this.problem?.errorCode === 'turnstile_failed') {
      return 'turnstile_failed';
    }
    return 'generic';
  }
}
